/**
 * 🔥 HOOK REALTIME ULTRA-ISOLÉ pour Big Data Chat
 * 
 * Inspiré de FluidChat et RealtimeManager
 * Features:
 * - Isolation complète par user_id + session_id
 * - Persistence automatique via Supabase Realtime
 * - Messages, Insights, Queries en temps réel
 * - Cleanup automatique
 * - Reconnexion automatique
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';
import logger from '../utils/logger';

// 🔇 LOGS COMPLÈTEMENT DÉSACTIVÉS EN PRODUCTION
const DEBUG = false;
const log = (...args: any[]) => {
  if (DEBUG && import.meta.env.DEV) logger.log(...args);
};
// ✅ FIX: logError aussi désactivé en prod (spam massif)
const logError = (...args: any[]) => {
  if (import.meta.env.DEV) logger.error(...args);
};

// ============================================
// TYPES
// ============================================

export interface BigDataChatMessage {
  id: string;
  session_id: string;
  user_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  query_sql?: string;
  result_count?: number;
  execution_time_ms?: number;
  confidence?: number;
  created_at: string;
}

export interface BigDataInsightRealtime {
  id: string;
  session_id: string;
  user_id: string;
  dataset_id: string;
  pattern_type: string;
  title: string;
  description: string;
  confidence_score: number;
  discovered_by: string;
  discovered_at: string;
  is_bookmarked: boolean;
}

export interface BigDataQueryRealtime {
  id: string;
  session_id: string;
  user_id: string;
  question: string;
  sql_query?: string;
  result_count?: number;
  execution_time_ms?: number;
  created_at: string;
}

interface UseBigDataChatRealtimeOptions {
  sessionId: string;
  userId: string;
  datasetId?: string;
  onMessage?: (message: BigDataChatMessage) => void;
  onInsight?: (insight: BigDataInsightRealtime) => void;
  onQuery?: (query: BigDataQueryRealtime) => void;
  enabled?: boolean;
}

// ============================================
// HOOK
// ============================================

export const useBigDataChatRealtime = (options: UseBigDataChatRealtimeOptions) => {
  const {
    sessionId,
    userId,
    datasetId,
    onMessage,
    onInsight,
    onQuery,
    enabled = true,
  } = options;

  // State
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messageCount, setMessageCount] = useState(0);
  const [insightCount, setInsightCount] = useState(0);
  const [queryCount, setQueryCount] = useState(0);

  // Refs pour les channels
  const messagesChannelRef = useRef<RealtimeChannel | null>(null);
  const insightsChannelRef = useRef<RealtimeChannel | null>(null);
  const queriesChannelRef = useRef<RealtimeChannel | null>(null);

  // ============================================
  // CLEANUP FUNCTION
  // ============================================
  const cleanup = useCallback(() => {
    log('[🔥 BigDataChatRealtime] Cleaning up channels...');

    if (messagesChannelRef.current) {
      supabase.removeChannel(messagesChannelRef.current);
      messagesChannelRef.current = null;
    }

    if (insightsChannelRef.current) {
      supabase.removeChannel(insightsChannelRef.current);
      insightsChannelRef.current = null;
    }

    if (queriesChannelRef.current) {
      supabase.removeChannel(queriesChannelRef.current);
      queriesChannelRef.current = null;
    }

    setIsConnected(false);
  }, []);

  // ============================================
  // SETUP REALTIME SUBSCRIPTIONS
  // ============================================
  useEffect(() => {
    if (!enabled || !sessionId || !userId) {
      log('[⚠️ BigDataChatRealtime] Disabled or missing params');
      return;
    }

    log('[🔥 BigDataChatRealtime] Setting up subscriptions...');
    log('   Session ID:', sessionId);
    log('   User ID:', userId);
    log('   Dataset ID:', datasetId);

    // ============================================
    // 1. MESSAGES SUBSCRIPTION
    // ============================================
    const messagesChannel = supabase
      .channel(`bigdata_messages:${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'big_data_queries', // Messages stockés dans big_data_queries
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          log('[📩 BigDataChatRealtime] New message:', payload.new);
          
          const newQuery = payload.new as BigDataQueryRealtime;
          
          // Incrémenter compteur
          setMessageCount((prev) => prev + 1);
          setQueryCount((prev) => prev + 1);

          // Callback
          if (onQuery) {
            onQuery(newQuery);
          }

          // Créer message assistant si nécessaire
          if (onMessage) {
            const message: BigDataChatMessage = {
              id: newQuery.id,
              session_id: newQuery.session_id,
              user_id: newQuery.user_id,
              role: 'assistant',
              content: `Query: ${newQuery.question}`,
              query_sql: newQuery.sql_query,
              result_count: newQuery.result_count,
              execution_time_ms: newQuery.execution_time_ms,
              created_at: newQuery.created_at,
            };
            onMessage(message);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          log('[✅ BigDataChatRealtime] Messages subscribed');
          setIsConnected(true);
        } else if (status === 'CHANNEL_ERROR') {
          logError('[❌ BigDataChatRealtime] Messages channel error');
          setError('Messages subscription error');
        } else if (status === 'TIMED_OUT') {
          logError('[⏱️ BigDataChatRealtime] Messages timed out');
          setError('Messages subscription timed out');
        }
      });

    messagesChannelRef.current = messagesChannel;

    // ============================================
    // 2. INSIGHTS SUBSCRIPTION (avec dataset_id)
    // ============================================
    if (datasetId) {
      const insightsChannel = supabase
        .channel(`bigdata_insights:${sessionId}:${datasetId}`)
        .on(
          'postgres_changes',
          {
            event: '*', // INSERT, UPDATE, DELETE
            schema: 'public',
            table: 'business_patterns',
            filter: `session_id=eq.${sessionId},dataset_id=eq.${datasetId}`,
          },
          (payload) => {
            log('[💡 BigDataChatRealtime] Insight change:', payload.eventType, payload.new || payload.old);

            if (payload.eventType === 'INSERT' && payload.new) {
              const newInsight = payload.new as BigDataInsightRealtime;
              
              // Incrémenter compteur
              setInsightCount((prev) => prev + 1);

              // Callback
              if (onInsight) {
                onInsight(newInsight);
              }
            } else if (payload.eventType === 'UPDATE' && payload.new) {
              // Mise à jour d'insight (bookmark, validation, etc.)
              if (onInsight) {
                onInsight(payload.new as BigDataInsightRealtime);
              }
            } else if (payload.eventType === 'DELETE' && payload.old) {
              // Insight supprimé
              log('[🗑️ BigDataChatRealtime] Insight deleted:', payload.old.id);
            }
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            log('[✅ BigDataChatRealtime] Insights subscribed');
          } else if (status === 'CHANNEL_ERROR') {
            logError('[❌ BigDataChatRealtime] Insights channel error');
          }
        });

      insightsChannelRef.current = insightsChannel;
    }

    // ============================================
    // 3. SESSION QUERIES SUBSCRIPTION (historique)
    // ============================================
    const queriesChannel = supabase
      .channel(`bigdata_queries:${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'big_data_queries',
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          log('[🔍 BigDataChatRealtime] New query logged:', payload.new);
          
          const newQuery = payload.new as BigDataQueryRealtime;
          setQueryCount((prev) => prev + 1);

          if (onQuery) {
            onQuery(newQuery);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          log('[✅ BigDataChatRealtime] Queries subscribed');
        } else if (status === 'CHANNEL_ERROR') {
          logError('[❌ BigDataChatRealtime] Queries channel error');
        }
      });

    queriesChannelRef.current = queriesChannel;

    // ============================================
    // CLEANUP
    // ============================================
    return () => {
      cleanup();
    };
  }, [sessionId, userId, datasetId, enabled, onMessage, onInsight, onQuery, cleanup]);

  // ============================================
  // PUBLIC API
  // ============================================
  return {
    isConnected,
    error,
    messageCount,
    insightCount,
    queryCount,
    cleanup,
  };
};

// ============================================
// HOOK SIMPLIFIÉ POUR INSIGHTS SEULEMENT
// ============================================

export const useBigDataInsightsRealtime = (
  datasetId: string,
  userId: string,
  onInsight: (insight: BigDataInsightRealtime) => void
) => {
  const [isConnected, setIsConnected] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!datasetId || !userId) return;

    log('[💡 InsightsRealtime] Subscribing to dataset insights:', datasetId);

    const channel = supabase
      .channel(`dataset_insights:${datasetId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'business_patterns',
          filter: `dataset_id=eq.${datasetId},user_id=eq.${userId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT' && payload.new) {
            log('[💡 InsightsRealtime] New insight detected');
            onInsight(payload.new as BigDataInsightRealtime);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          log('[✅ InsightsRealtime] Subscribed successfully');
          setIsConnected(true);
        } else if (status === 'CHANNEL_ERROR') {
          logError('[❌ InsightsRealtime] Subscription error');
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      setIsConnected(false);
    };
  }, [datasetId, userId, onInsight]);

  return { isConnected };
};


